import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { PessoaFotoDialogComponent } from '@/shared/components/pessoa/pessoa-foto-dialog/pessoa-foto-dialog';
import { PaginatorModule } from 'primeng/paginator';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { LayoutService } from '@/layout/service/layout.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { AuthService } from '@/core/auth/auth.service';
import { RolesAdminService } from '@/core/api/sicom-api/sistema/roles/roles-admin.service';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { EstadoRotulo } from '@/shared/components/layout/estado-rotulo/estado-rotulo';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { UF_CFM } from '@/core/permissions/permission.model';
import { PermissionService } from '@/core/permissions';
import { PerfisService } from '@/core/api/sicom-api/sistema/perfis/perfis.service';
import { Perfil, PerfilResumo, UsuarioPerfil } from '@/core/api/sicom-api/sistema/perfis/perfis.model';
import { agruparRoles, FuncionalidadeGrupo, ModuloGrupo } from '../roles-agrupamento.util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';

/**
 * Texto de erro para o usuário: prefere a explicação que o servidor mandou (`detail` e depois
 * `title` do `CfmErrorDto`), cai na mensagem do próprio erro e, só em último caso, no genérico.
 * Defensivo de propósito — o erro chega como `unknown` e nem toda falha é uma resposta HTTP.
 */
function mensagemDoErro(err: unknown, generico: string): string {
    const e = err as { error?: { detail?: string; title?: string } | null; message?: string } | null | undefined;
    return e?.error?.detail?.trim() || e?.error?.title?.trim() || e?.message?.trim() || generico;
}

@Component({
    selector: 'app-atribuir-roles',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        ButtonModule,
        InputTextModule,
        TableModule,
        PessoaAvatarComponent,
        PessoaFotoDialogComponent,
        PaginatorModule,
        ToggleSwitchModule,
        Dialog,
        ConfirmDialogModule,
        TooltipModule,
        TableViewSwitcherComponent,
        CardContainer,
        CardContent,
        CanDirective,
        EstadoRotulo,
        SelectEstado,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [ConfirmationService],
    templateUrl: './atribuir-roles.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Atribuição de roles a um usuário: lista usuários, mostra o catálogo agrupado e persiste o diff de seleção.
 *
 * Perfis do usuário (modelo N-perfis): a tela carrega os perfis vinculados sozinha, via
 * `PerfisService.perfisDoUsuario`, ao selecionar/trocar de usuário — com guarda de corrida (uma resposta de
 * um `userId` que não é mais o ativo não sobrescreve a lista). Os perfis **ativos** viram chips removíveis no
 * cabeçalho; cada × chama `PerfisService.removerDoUsuario` (desvincula o usuário daquele perfil, tirando no
 * Keycloak só as roles que ele concedeu e preservando as standalone). Os perfis **excluídos por lápide**
 * aparecem só num aviso, listando as roles que foram mantidas para o usuário. Tanto aplicar quanto remover um
 * perfil refazem o fetch (`carregarPerfisUsuario`) — os chips aparecem/somem na hora, sem depender de um F5.
 */
export class AtribuirRoles implements OnInit {
    private readonly rolesService = inject(RolesAdminService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly perfisService = inject(PerfisService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirm = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutService = inject(LayoutService);
    private readonly auth = inject(AuthService);
    private readonly perm = inject(PermissionService);

    /** Informado → pula o seletor (modo embutido futuro no cadastro do usuário). */
    userId = input<string | null>(null);
    /** Modo modal: identificação do usuário (Nome/UF/avatar/perfil atual) para o header. */
    nomeUsuario = input<string | null>(null);
    ufUsuario = input<string | null>(null);
    avatarUsuario = input<string | null>(null);

    protected readonly usuarios = signal<UsuarioResumoResponse[]>([]);
    protected readonly filtroUsuario = signal('');
    /** UF do seletor de usuários. `BR` (ou vazio) = todas, e é o que o CFM vê ao abrir. */
    protected readonly filtroUf = signal('');
    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.perm.isCfm);
    protected readonly usuarioSel = signal<UsuarioResumoResponse | null>(null);
    protected readonly userIdAtivo = computed(() => this.userId() ?? this.usuarioSel()?.userId ?? null);
    protected readonly nomeAtivo = computed(() => this.nomeUsuario() ?? this.usuarioSel()?.nmSolicitante ?? null);
    protected readonly ufAtivo = computed(() => this.ufUsuario() ?? this.usuarioSel()?.ufSolicitante ?? null);
    protected readonly avatarAtivo = computed(() => this.avatarUsuario() ?? this.usuarioSel()?.avatar ?? null);

    /**
     * Perfis vinculados ao usuário ativo (modelo N-perfis, cada um com suas roles e a flag `excluido`).
     * Carregado por {@link carregarPerfisUsuario} ao selecionar/trocar de usuário e refeito após aplicar/remover.
     */
    private readonly perfisUsuario = signal<UsuarioPerfil[]>([]);
    /** Perfis vigentes → chips removíveis no cabeçalho. */
    protected readonly perfisAtivos = computed(() => this.perfisUsuario().filter((p) => !p.excluido));
    /** Perfis excluídos por lápide → só o aviso (roles preservadas), nunca chip removível. */
    protected readonly perfisExcluidos = computed(() => this.perfisUsuario().filter((p) => p.excluido));

    protected readonly viewMode = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly cardPage = signal(0);
    protected readonly cardPageSize = signal(15);
    protected readonly usuariosPaginados = computed(() => {
        const inicio = this.cardPage() * this.cardPageSize();
        return this.usuariosFiltrados().slice(inicio, inicio + this.cardPageSize());
    });

    protected readonly rolePermissaoAdministrar = PERMISSOES.configuracoes.usuarios.permissoes.administrar;

    protected readonly grupos = signal<ModuloGrupo[]>([]);
    protected readonly selecionadas = signal<Set<string>>(new Set());
    /**
     * Roles que o servidor tem hoje: base do diff do Salvar e do que o "Remover todas roles" apaga.
     *
     * <p>Vem de `GET /api/usuarios/{id}/roles`, que o backend **já filtra** pelo alcance do operador
     * (`EscopoRolesService`). É essa filtragem que torna "Remover todas" seguro para um operador de
     * UF: ele nunca vê — logo nunca apaga — uma role que o CFM concedeu e que ele não alcança.
     * Desfazer o filtro no backend reabre perda de dados silenciosa.
     */
    private readonly originais = signal<Set<string>>(new Set());
    protected readonly carregando = signal(false);
    protected readonly erroCarregar = signal(false);
    protected readonly salvando = signal(false);

    // ── Kanban: módulo/funcionalidade selecionados ──
    protected readonly moduloSel = signal<string | null>(null);
    protected readonly funcSel = signal<string | null>(null);
    protected readonly moduloAtual = computed(() => this.grupos().find((m) => m.modulo === this.moduloSel()) ?? null);
    protected readonly funcAtual = computed(() => this.moduloAtual()?.funcionalidades.find((f) => f.funcionalidade === this.funcSel()) ?? null);

    protected readonly usuariosFiltrados = computed(() => {
        const t = this.filtroUsuario().toLowerCase().trim();
        return this.usuarios().filter((u) => !!u.userId && (!t || (u.nmSolicitante ?? '').toLowerCase().includes(t) || (u.userId ?? '').toLowerCase().includes(t)));
    });

    protected readonly temAlteracoes = computed(() => {
        const sel = this.selecionadas();
        const orig = this.originais();
        if (sel.size !== orig.size) return true;
        for (const r of sel) if (!orig.has(r)) return true;
        return false;
    });

    // ── Aplicar perfil: atalho aditivo — soma as roles do perfil às do usuário, sem remover nenhuma ──
    protected readonly dialogPerfilAberto = signal(false);
    protected readonly perfis = signal<PerfilResumo[]>([]);

    /**
     * Os perfis oferecidos, agrupados pela UF dona de cada um — nacional primeiro, regionais em
     * ordem alfabética.
     *
     * <p>Existe porque numa lista corrida o nome do perfil não diz de onde ele vem, e a unicidade
     * de nome é global só por decisão de projeto: "Financeiro" nacional e "Financeiro" de um CRM
     * seriam indistinguíveis. Para o operador do CFM, que é quem enxerga mais de uma UF de uma vez,
     * a UF é a informação que decide qual dos dois aplicar.</p>
     *
     * <p>Cada grupo entrega só a sigla; quem a transforma em bandeira e nome por extenso é o
     * `EstadoRotulo`, o mesmo componente que o catálogo usa — assim as duas telas não podem
     * discordar sobre como uma UF se chama.</p>
     */
    protected readonly perfisPorUf = computed(() => {
        const porUf = new Map<string, PerfilResumo[]>();
        for (const p of this.perfis()) {
            const uf = (p.uf ?? '').toUpperCase();
            const doGrupo = porUf.get(uf);
            if (doGrupo) doGrupo.push(p);
            else porUf.set(uf, [p]);
        }
        return [...porUf.entries()]
            // Dentro do grupo, por nome — `pt-BR` porque os nomes têm acento e, em ordenação
            // binária, "Órgãos" cairia depois de "Sistema". Mesma ordem do catálogo.
            .map(([uf, perfis]) => ({ uf, perfis: [...perfis].sort((a, b) => (a.nmPerfil ?? '').localeCompare(b.nmPerfil ?? '', 'pt-BR')) }))
            .sort((a, b) => {
                if (a.uf === UF_CFM) return -1;
                if (b.uf === UF_CFM) return 1;
                return a.uf.localeCompare(b.uf);
            });
    });

    protected readonly carregandoPerfis = signal(false);
    protected readonly perfilSel = signal<Perfil | null>(null);
    protected readonly carregandoPerfilSel = signal(false);
    protected readonly aplicandoPerfil = signal(false);
    /** Roles do perfil escolhido que o usuário ainda não tem — prévia informativa; nada é calculado além disso, o backend decide o que de fato aplicar. */
    protected readonly rolesPerfilAdicionadas = computed(() => {
        const perfil = this.perfilSel();
        return perfil ? perfil.roles.filter((r) => !this.originais().has(r)) : [];
    });

    // ── Remover perfil: desvincula o usuário do perfil, preservando roles standalone ──
    protected readonly removendoPerfil = signal(false);

    // ── Remover todas as roles: ação completa em si mesma, sem passar pelo Salvar ──
    protected readonly removendoTodas = signal(false);
    /** O usuário selecionado é o próprio administrador logado. */
    protected readonly ehProprioUsuario = computed(() => {
        const logado = this.auth.usuarioLogado()?.userId;
        return !!logado && logado === this.userIdAtivo();
    });
    /**
     * Habilita o botão. Bloqueado no próprio usuário — zerar as próprias roles tira o
     * `configuracoes.usuarios.permissoes.administrar` e fecha a porta desta tela para quem clicou — e enquanto
     * houver uma gravação em voo (Salvar ou a própria remoção), para não disparar duas.
     *
     * <p>A pergunta "há o que remover?" é sobre o **servidor** ({@link originais}), não sobre os
     * toggles: quem desmarcou tudo na tela sem salvar continua com as roles lá, e o botão precisa
     * poder apagá-las.
     */
    protected readonly podeRemoverTudo = computed(() => {
        if (this.ehProprioUsuario() || this.salvando() || this.removendoTodas()) return false;
        return this.originais().size > 0 || this.perfisUsuario().length > 0;
    });

    // ── Exibir roles: expansão inline (accordion) das roles do perfil, agrupadas por módulo ──
    // Substitui a antiga prévia por hover em popover (Task 13): o botão "Exibir roles" da linha
    // expande o detalhe logo abaixo, dentro do próprio modal, em vez de um painel flutuante
    // desancorado. Só um perfil fica expandido por vez.
    protected readonly perfilExpandidoId = signal<number | null>(null);
    protected readonly gruposExpandidos = signal<ModuloGrupo[]>([]);
    protected readonly erroExpansao = signal(false);
    /**
     * Id do perfil cuja busca está em voo (`null` = nenhuma) — não um booleano solto. Ao trocar de
     * perfil expandido para outro ainda não cacheado, este id é sobrescrito; a resposta atrasada da
     * busca anterior então falha a comparação `carregandoPerfilId() === p.id` no seu próprio
     * `next`/`error` e é descartada sem efeito. `carregandoExpansao` (exposto) é só true quando o id
     * em voo bate com o perfil que está de fato expandido agora — resolve de uma vez tanto o caso de
     * uma resposta tardia "roubar" o desligamento do spinner de outro perfil quanto o caso do ramo de
     * cache (que não passa por busca nenhuma) herdar um carregando alheio.
     */
    private readonly carregandoPerfilId = signal<number | null>(null);
    protected readonly carregandoExpansao = computed(() => this.carregandoPerfilId() !== null && this.carregandoPerfilId() === this.perfilExpandidoId());
    /** Cache de `Perfil` (com roles) por id — evita rebuscar ao reexpandir um perfil já visto. */
    private readonly rolesCache = new Map<number, Perfil>();

    constructor() {
        effect(() => {
            const id = this.userIdAtivo();
            if (id) {
                this.carregarRoles(id);
                this.carregarPerfisUsuario(id);
            }
        });
    }

    ngOnInit(): void {
        // Regional: a UF do operador já é conhecida sem ida ao servidor — o filtro nasce nela, travado.
        // CFM: nasce em `BR`, que o backend lê como "todas".
        this.filtroUf.set(this.ufOperador() || UF_CFM);
        if (!this.userId()) this.carregarUsuarios();
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.perm.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    /**
     * Usuários oferecidos no seletor. O CFM vê todos, ou só os da UF que escolher no filtro; o regional
     * recebe só os da própria UF, sem escolha.
     *
     * <p>Isto é conveniência de UI, **não** o gate: quem recusa escrita sobre usuário de outra UF é o
     * `EscopoRolesService` no backend. O filtro existe para o seletor não oferecer gente que o
     * operador tomaria 403 ao tentar editar.
     */
    private carregarUsuarios(): void {
        // Travado, a UF vem do escopo e não do select — que apenas a exibe. Livre, vale a escolha do
        // CFM; `BR` (ou vazio) significa "todas" e vai como `undefined`, sem parâmetro na query.
        const escolhida = this.ufTravada() ? this.ufOperador() : this.filtroUf().trim().toUpperCase();
        const uf = escolhida && escolhida !== UF_CFM ? escolhida : undefined;
        this.usuarioService
            .listar({ size: 200, uf })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (p) => this.usuarios.set(p.content),
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar usuários' })
            });
    }

    private carregarRoles(userId: string): void {
        this.carregando.set(true);
        this.erroCarregar.set(false);
        forkJoin({ todas: this.rolesService.listarRoles(), doUsuario: this.rolesService.rolesDoUsuario(userId) })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ todas, doUsuario }) => {
                    const grupos = agruparRoles(todas);
                    this.grupos.set(grupos);
                    this.originais.set(new Set(doUsuario.map((r) => r.name)));
                    this.selecionadas.set(new Set(this.originais()));
                    this.selecionarModulo(grupos[0] ?? null);
                    this.carregando.set(false);
                },
                error: () => {
                    this.erroCarregar.set(true);
                    this.carregando.set(false);
                }
            });
    }

    /**
     * Carrega os perfis do usuário (chips ativos + aviso de excluídos). Guarda de corrida: captura o `userId`
     * do pedido e só aplica a resposta se ele ainda for o usuário ativo — uma resposta de um usuário anterior
     * (troca rápida) nunca sobrescreve a lista do atual.
     */
    private carregarPerfisUsuario(userId: string): void {
        this.perfisService
            .perfisDoUsuario(userId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfis) => {
                    if (this.userIdAtivo() === userId) this.perfisUsuario.set(perfis);
                },
                error: () => {
                    if (this.userIdAtivo() === userId) this.perfisUsuario.set([]);
                }
            });
    }

    /** Filtrar: a UF é filtro de servidor, então rebusca. O texto já filtra em memória enquanto se digita. */
    protected aplicarFiltros(): void {
        this.cardPage.set(0);
        this.carregarUsuarios();
    }

    protected limparFiltros(): void {
        this.filtroUsuario.set('');
        // Limpar não destrava a UF do regional: ela volta para a dele, nunca para "todas".
        this.filtroUf.set(this.ufOperador() || UF_CFM);
        this.cardPage.set(0);
        this.carregarUsuarios();
    }

    protected selecionarUsuario(u: UsuarioResumoResponse): void {
        this.usuarioSel.set(u);
    }

    protected onCardPageChange(event: { first?: number; rows?: number }): void {
        const rows = event.rows ?? this.cardPageSize();
        this.cardPageSize.set(rows);
        this.cardPage.set(Math.floor((event.first ?? 0) / rows));
    }

    protected iniciais(nome: string): string {
        const base = (nome || '?').trim();
        return base
            .split(/\s+/)
            .slice(0, 2)
            .map((p) => p[0])
            .join('')
            .toUpperCase();
    }
    protected trocarUsuario(): void {
        this.usuarioSel.set(null);
        this.grupos.set([]);
        this.selecionadas.set(new Set());
        this.originais.set(new Set());
        this.moduloSel.set(null);
        this.funcSel.set(null);
        this.perfisUsuario.set([]);
    }

    protected selecionarModulo(mod: ModuloGrupo | null): void {
        this.moduloSel.set(mod?.modulo ?? null);
        this.funcSel.set(mod?.funcionalidades[0]?.funcionalidade ?? null);
    }
    protected selecionarFunc(f: FuncionalidadeGrupo): void {
        this.funcSel.set(f.funcionalidade);
    }

    protected marcada(role: string): boolean {
        return this.selecionadas().has(role);
    }
    protected toggle(role: string): void {
        const s = new Set(this.selecionadas());
        if (s.has(role)) s.delete(role);
        else s.add(role);
        this.selecionadas.set(s);
    }

    protected crudTodas(f: FuncionalidadeGrupo): boolean {
        return f.crud.length > 0 && f.crud.every((a) => this.marcada(a.role));
    }
    protected toggleCrud(f: FuncionalidadeGrupo): void {
        const ligar = !this.crudTodas(f);
        const s = new Set(this.selecionadas());
        for (const a of f.crud) {
            if (ligar) s.add(a.role);
            else s.delete(a.role);
        }
        this.selecionadas.set(s);
    }

    /**
     * Aplica o diff de seleção: remove as desmarcadas (**em lote**, uma requisição) e atribui as novas.
     *
     * <p>Sem `catchError` por operação: uma falha precisa chegar ao usuário como erro, não ser
     * engolida por baixo de uma mensagem de sucesso.
     */
    protected salvar(): void {
        const id = this.userIdAtivo();
        if (!id) return;
        const sel = this.selecionadas();
        const orig = this.originais();
        const adicionar = [...sel].filter((r) => !orig.has(r));
        const remover = [...orig].filter((r) => !sel.has(r));
        if (!adicionar.length && !remover.length) return;
        this.salvando.set(true);
        (remover.length ? this.rolesService.removerVarias(id, remover) : of(void 0))
            .pipe(
                switchMap(() => (adicionar.length ? this.rolesService.atribuir(id, adicionar) : of(void 0))),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Roles atualizadas.' });
                    this.originais.set(new Set(sel));
                    this.salvando.set(false);
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao salvar' });
                    this.salvando.set(false);
                }
            });
    }

    /**
     * Pede confirmação — nomeando o usuário, a quantidade de roles e os perfis afetados — e, aceitando,
     * executa a remoção total ali mesmo. É uma ação completa em si: não depende do Salvar.
     */
    protected confirmarRemoverTodas(): void {
        if (!this.podeRemoverTudo()) return;
        const qtd = this.originais().size;
        const perfis = this.perfisUsuario();
        const nomes = perfis.map((p) => `«${p.nmPerfil}»`).join(', ');
        const sobrePerfis = perfis.length ? ` ${perfis.length === 1 ? `O perfil ${nomes} também será desvinculado` : `Os perfis ${nomes} também serão desvinculados`}.` : '';
        this.confirm.confirm({
            header: 'Remover todas as roles',
            message: `Remover as ${qtd} role(s) de ${this.nomeAtivo() || this.userIdAtivo()}?${sobrePerfis} Esta ação é aplicada imediatamente e não pode ser desfeita.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            accept: () => this.removerTodas()
        });
    }

    /**
     * Zera o usuário: desvincula **todos** os perfis (ativos e excluídos por lápide — deixar um
     * vínculo de pé num usuário sem role nenhuma seria um chip mentindo na tela) e só então apaga,
     * numa chamada em lote, as roles que o servidor ainda tinha.
     *
     * <p>Perfis primeiro porque desvincular já retira do Keycloak o que só aquele perfil sustentava;
     * o lote seguinte limpa o que sobrou (as standalone). O inverso deixaria o backend recalculando
     * proveniência sobre um estado alterado pela metade.
     *
     * <p>No fim recarrega roles e perfis do servidor: a tela passa a refletir o que de fato ficou lá,
     * e qualquer edição local não salva é descartada junto — como no "Aplicar perfil".
     */
    private removerTodas(): void {
        const id = this.userIdAtivo();
        if (!id) return;
        const perfis = this.perfisUsuario();
        const roles = [...this.originais()];
        this.removendoTodas.set(true);
        // Cada perna tolera a própria falha (`catchError` → `false`). Sem isso, um único 403 —
        // desvincular é gateado pela UF do PERFIL, e um perfil nacional num usuário regional é
        // exatamente o caso — abortava a cadeia inteira: as pernas que já tinham passado ficavam
        // aplicadas, a remoção das roles standalone nunca rodava, e o operador lia "Erro ao remover
        // as roles" para um sucesso parcial. Contamos as falhas para dizer a verdade no fim.
        const desvincular$: Observable<boolean[]> = perfis.length
            ? forkJoin(
                  perfis.map((p) =>
                      this.perfisService.removerDoUsuario(id, p.idWebPerfil).pipe(
                          map(() => true),
                          catchError(() => of(false))
                      )
                  )
              )
            : of([]);
        desvincular$
            .pipe(
                switchMap((resultados) => {
                    const falhas = resultados.filter((ok) => !ok).length;
                    return (roles.length ? this.rolesService.removerVarias(id, roles) : of(void 0)).pipe(map(() => falhas));
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (falhas) => {
                    this.removendoTodas.set(false);
                    this.carregarRoles(id);
                    this.carregarPerfisUsuario(id);
                    const alvo = this.nomeAtivo() || id;
                    if (falhas > 0) {
                        this.notificacoes.notificar({
                            toast: false,
                            severity: 'warn',
                            summary: `Remoção parcial em ${alvo}: ${falhas} perfil(is) não pôde(m) ser desvinculado(s) — provavelmente de outra UF. As demais roles foram removidas.`
                        });
                        return;
                    }
                    this.notificacoes.notificar({ severity: 'success', summary: `Todas as roles de ${alvo} foram removidas.` });
                },
                error: () => {
                    this.removendoTodas.set(false);
                    this.carregarRoles(id);
                    this.carregarPerfisUsuario(id);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao remover as roles' });
                }
            });
    }

    /** Abre o diálogo de "Aplicar perfil" e carrega os perfis ativos. Sem usuário ativo, não faz nada. */
    protected abrirDialogPerfil(): void {
        if (!this.userIdAtivo()) return;
        this.dialogPerfilAberto.set(true);
        this.perfilSel.set(null);
        this.perfilExpandidoId.set(null);
        this.carregarPerfis();
    }

    private carregarPerfis(): void {
        this.carregandoPerfis.set(true);
        this.perfisService
            .listar('S')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfis) => {
                    // R1 no cliente: perfil nacional serve a todos; perfil regional só ao usuário
                    // da mesma UF. É afordância, não segurança — o backend recusa com 409 de todo
                    // jeito, e a lista pode envelhecer entre o carregamento e o clique.
                    // Comparação case-insensitive dos dois lados, como o `equalsIgnoreCase` do
                    // backend: divergir aqui só esconderia um perfil legítimo do operador.
                    const uf = this.ufAtivo()?.toUpperCase() ?? null;
                    this.perfis.set(perfis.filter((p) => p.uf?.toUpperCase() === UF_CFM || p.uf?.toUpperCase() === uf));
                    this.carregandoPerfis.set(false);
                },
                error: () => {
                    this.carregandoPerfis.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar perfis' });
                }
            });
    }

    /**
     * Alterna a expansão inline das roles do perfil (accordion: só um por vez). Clicar no perfil já
     * expandido recolhe sem rebuscar; expandir outro recolhe o anterior automaticamente (é só trocar
     * o id corrente). Usa {@link rolesCache} para não rebuscar um perfil já visto nesta sessão do modal.
     */
    protected toggleExibirRoles(p: PerfilResumo): void {
        if (this.perfilExpandidoId() === p.id) {
            this.perfilExpandidoId.set(null);
            return;
        }
        this.perfilExpandidoId.set(p.id);
        this.erroExpansao.set(false);

        const emCache = this.rolesCache.get(p.id);
        if (emCache) {
            // ramo do cache: nada em voo para ESTE perfil — desliga explicitamente em vez de deixar
            // como está, senão herdaria o "carregando" de uma busca alheia ainda pendente (ex.: trocar
            // para um perfil já cacheado enquanto o anterior, não cacheado, ainda está buscando).
            this.carregandoPerfilId.set(null);
            this.gruposExpandidos.set(agruparRoles(emCache.roles.map((r) => ({ name: r }))));
            return;
        }

        this.gruposExpandidos.set([]);
        this.carregandoPerfilId.set(p.id);
        this.perfisService
            .buscar(p.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfil) => {
                    this.rolesCache.set(p.id, perfil);
                    // guarda contra troca de linha antes desta resposta voltar: só desliga o
                    // "carregando"/aplica o resultado se este perfil ainda for o que está em voo/expandido —
                    // uma resposta atrasada de um perfil abandonado não pode mexer no estado de outro.
                    if (this.carregandoPerfilId() === p.id) this.carregandoPerfilId.set(null);
                    if (this.perfilExpandidoId() === p.id) this.gruposExpandidos.set(agruparRoles(perfil.roles.map((r) => ({ name: r }))));
                },
                error: () => {
                    if (this.carregandoPerfilId() === p.id) this.carregandoPerfilId.set(null);
                    if (this.perfilExpandidoId() === p.id) this.erroExpansao.set(true);
                }
            });
    }

    /** Busca o detalhe (roles) do perfil escolhido para montar a prévia informativa. */
    protected selecionarPerfil(p: PerfilResumo): void {
        this.perfilExpandidoId.set(null);
        this.carregandoPerfilSel.set(true);
        this.perfisService
            .buscar(p.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (perfil) => {
                    this.perfilSel.set(perfil);
                    this.carregandoPerfilSel.set(false);
                },
                error: () => {
                    this.carregandoPerfilSel.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar perfil' });
                }
            });
    }

    protected fecharDialogPerfil(): void {
        this.dialogPerfilAberto.set(false);
        this.perfilSel.set(null);
        this.perfilExpandidoId.set(null);
    }

    /**
     * Chama o endpoint aditivo/rastreado de aplicar perfil (soma — nunca remove roles). O front não recalcula
     * o diff: reflete o resultado do servidor recarregando as roles e **refazendo o fetch dos perfis** do
     * usuário — assim o chip do perfil recém-aplicado aparece na hora, sem depender de um F5.
     */
    protected confirmarAplicarPerfil(): void {
        const userId = this.userIdAtivo();
        const perfil = this.perfilSel();
        if (!userId || !perfil) return;
        this.aplicandoPerfil.set(true);
        this.perfisService
            .aplicar(perfil.id, userId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.aplicandoPerfil.set(false);
                    this.fecharDialogPerfil();
                    this.carregarRoles(userId);
                    this.carregarPerfisUsuario(userId);
                    this.notificacoes.notificar({ severity: 'success', summary: `${res.adicionadas.length} role(s) adicionada(s) pelo perfil "${perfil.nmPerfil}".` });
                },
                error: () => {
                    this.aplicandoPerfil.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao aplicar perfil' });
                }
            });
    }

    /**
     * Pede confirmação — nomeando o perfil clicado e o usuário — e, aceitando, desvincula o usuário ativo
     * daquele perfil específico (o × do chip). Só perfis ativos têm chip/×; perfis excluídos por lápide
     * ficam só no aviso e não podem ser removidos por aqui.
     */
    protected confirmarRemoverPerfil(perfil: UsuarioPerfil): void {
        const userId = this.userIdAtivo();
        if (!userId) return;
        this.confirm.confirm({
            header: 'Remover perfil',
            message: `Remover o perfil "${perfil.nmPerfil}" de ${this.nomeAtivo() || userId}? Só as roles concedidas por ele serão retiradas — roles atribuídas diretamente ao usuário não são afetadas.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            accept: () => this.removerPerfil(userId, perfil)
        });
    }

    /**
     * O `perfil` é o capturado no momento da confirmação — a notificação de sucesso nomeia exatamente o
     * perfil que este componente sabia que o usuário tinha (e que o DELETE, ao responder sem erro, de fato
     * desvinculou). No sucesso refaz o fetch dos perfis (o chip some na hora) e recarrega as roles.
     *
     * <p>No erro, mostra a mensagem que o servidor devolveu, não um texto genérico. A lista de perfis
     * do usuário é gateada pela UF do **usuário**, mas o DELETE é gateado pela UF do **perfil**: um
     * operador regional enxerga o × de um perfil nacional no usuário dele e leva 403 ao clicar. Como
     * `UsuarioPerfil` não carrega a UF do perfil, a tela não tem como prever qual × vai falhar — a
     * razão do servidor é a única explicação disponível ao operador.
     */
    private removerPerfil(userId: string, perfil: UsuarioPerfil): void {
        this.removendoPerfil.set(true);
        this.perfisService
            .removerDoUsuario(userId, perfil.idWebPerfil)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.removendoPerfil.set(false);
                    this.carregarPerfisUsuario(userId);
                    this.carregarRoles(userId);
                    this.notificacoes.notificar({ severity: 'success', summary: `Perfil "${perfil.nmPerfil}" removido de ${this.nomeAtivo() || userId}.` });
                },
                error: (err: unknown) => {
                    this.removendoPerfil.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: mensagemDoErro(err, 'Erro ao remover perfil') });
                }
            });
    }

    /** Foto em exibição no diálogo de ampliação; `null` o mantém fechado. */
    protected readonly fotoAmpliada = signal<string | null>(null);
    protected readonly nomeFotoAmpliada = signal('');

    /** Amplia a foto que o avatar emitiu, anunciando de quem ela é. */
    protected ampliarFoto(url: string, nome: string): void {
        this.nomeFotoAmpliada.set(nome ?? '');
        this.fotoAmpliada.set(url);
    }
}
